import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import CircularProgress from '@mui/joy/CircularProgress';
import Drawer from '@mui/joy/Drawer';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';

import request from '../helpers/request';

const toNormalizedType = (value) => (typeof value === 'string' ? value.toLowerCase() : '');

const resolveTemplateType = (template, fallbackType = '') => {
  const resolvedType =
    template?.settings?.type ??
    template?.type ??
    template?.policy_type ??
    template?.policy?.type ??
    fallbackType;

  return typeof resolvedType === 'string' ? resolvedType : '';
};

const resolveTemplatePolicy = (template) => {
  const resolvedPolicy = template?.policy ?? template?.template?.policy ?? {};
  return resolvedPolicy && typeof resolvedPolicy === 'object' ? resolvedPolicy : {};
};

const resolveTemplateSettings = (template) => {
  const resolvedSettings = template?.settings ?? template?.template?.settings ?? {};
  return resolvedSettings && typeof resolvedSettings === 'object' ? resolvedSettings : {};
};

const resolveTemplateFlow = (template) => {
  const resolvedFlow =
    template?.flow ??
    template?.policy?.flow ??
    template?.template?.flow ??
    template?.template?.policy?.flow;

  if (resolvedFlow === undefined || resolvedFlow === null) return undefined;
  return typeof resolvedFlow === 'object' ? resolvedFlow : undefined;
};

const normalizeTemplates = (data) => {
  if (Array.isArray(data)) {
    return data.filter((template) => template && typeof template === 'object');
  }

  if (data && typeof data === 'object') {
    return [data];
  }

  return [];
};

const safeClone = (value, fallback) => {
  if (value === undefined || value === null) return fallback;

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
};

const getUniquePolicyName = (rawName, existingPolicies = []) => {
  const baseName = typeof rawName === 'string' ? rawName.trim() : '';
  if (!baseName) return '';

  const existingNames = new Set(
    existingPolicies
      .map((policy) => (typeof policy?.name === 'string' ? policy.name.trim().toLowerCase() : ''))
      .filter(Boolean)
  );

  if (!existingNames.has(baseName.toLowerCase())) {
    return baseName;
  }

  let copyIndex = 1;
  let candidate = `${baseName} copy`;

  while (existingNames.has(candidate.toLowerCase())) {
    copyIndex += 1;
    candidate = `${baseName} copy ${copyIndex}`;
  }

  return candidate;
};

const CreatePolicyDrawer = ({
  open,
  onClose,
  taskTypes = [],
  taskTypesLoading = false,
  policies = [],
  onSubmitPolicy,
}) => {
  const [errors, setErrors] = useState({ name: '', type: '' });
  const [selectedType, setSelectedType] = useState('');
  const [policyName, setPolicyName] = useState('');
  const [selectedPolicyName, setSelectedPolicyName] = useState('');
  const [selectedLinkedPolicyId, setSelectedLinkedPolicyId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const compliancePolicies = useMemo(
    () => policies.filter((policy) => toNormalizedType(policy.settings?.type) === 'compliance'),
    [policies]
  );

  const filteredTemplates = useMemo(() => {
    const templatesWithNames = templates.filter(
      (template) => typeof template.name === 'string' && template.name.trim().length > 0
    );

    const selectedTypeNormalized = toNormalizedType(selectedType);
    if (!selectedTypeNormalized) {
      return templatesWithNames;
    }

    return templatesWithNames.filter(
      (template) =>
        toNormalizedType(resolveTemplateType(template, selectedType)) === selectedTypeNormalized
    );
  }, [templates, selectedType]);

  const resolveTaskTypeValue = useCallback(
    (typeValue) => {
      if (typeof typeValue !== 'string' || typeValue.trim().length === 0) return '';

      const matchedTaskType = taskTypes.find(
        (taskType) => toNormalizedType(taskType?.type) === toNormalizedType(typeValue)
      );

      return matchedTaskType?.type || typeValue;
    },
    [taskTypes]
  );

  const resetState = useCallback(() => {
    setErrors({ name: '', type: '' });
    setSelectedType('');
    setPolicyName('');
    setSelectedPolicyName('');
    setSelectedLinkedPolicyId(null);
    setTemplates([]);
    setTemplatesLoading(false);
    setTemplatesError('');
    setSelectedTemplate(null);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const clearErrors = useCallback(() => {
    setErrors({ name: '', type: '' });
  }, []);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const fetchTemplates = async () => {
      setTemplatesLoading(true);
      setTemplatesError('');

      try {
        const response = await request.get('/api/v1/compliance/policy/templates/');
        if (!isMounted) return;
        setTemplates(normalizeTemplates(response.data));
      } catch (error) {
        if (!isMounted) return;
        console.error('Failed to fetch policy templates:', error);
        setTemplates([]);
        setTemplatesError('Failed to load templates.');
      } finally {
        if (isMounted) {
          setTemplatesLoading(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      isMounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!selectedType) return;

    const normalizedSelectedType = resolveTaskTypeValue(selectedType);
    if (normalizedSelectedType !== selectedType) {
      setSelectedType(normalizedSelectedType);
    }
  }, [selectedType, resolveTaskTypeValue]);

  const handleTypeChange = (_, newValue) => {
    const nextType = newValue || '';

    setSelectedType(nextType);
    setSelectedPolicyName('');
    setSelectedLinkedPolicyId(null);
    clearErrors();

    const selectedTemplateType = resolveTemplateType(
      selectedTemplate,
      selectedType || 'Compliance'
    );

    if (
      selectedTemplate &&
      toNormalizedType(selectedTemplateType) !== toNormalizedType(nextType)
    ) {
      setSelectedTemplate(null);
    }
  };

  const handleApplyTemplate = (template) => {
    if (!template) return;

    const templateTypeRaw = resolveTemplateType(template, selectedType || 'Compliance');
    const templateType = resolveTaskTypeValue(templateTypeRaw || 'Compliance');

    if (!templateType || toNormalizedType(templateType) === 'evaluation') {
      return;
    }

    const templateName = typeof template.name === 'string' ? template.name : '';
    const uniqueTemplateName = getUniquePolicyName(templateName, policies);

    setSelectedTemplate(template);
    setSelectedType(templateType);
    setPolicyName(uniqueTemplateName);
    setSelectedPolicyName('');
    setSelectedLinkedPolicyId(null);
    clearErrors();
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const policyType = selectedType;
    const isEvaluation = toNormalizedType(policyType) === 'evaluation';
    const name = isEvaluation ? selectedPolicyName : policyName.trim();

    let hasError = false;
    const nextErrors = { name: '', type: '' };

    if (!policyType) {
      nextErrors.type = 'Please select a policy type.';
      hasError = true;
    }

    if (isEvaluation) {
      if (!name) {
        nextErrors.name = 'Please select a compliance policy.';
        hasError = true;
      }
    } else {
      if (!name) {
        nextErrors.name = 'Policy name cannot be empty or whitespace.';
        hasError = true;
      } else if (!/^[\w\s-]+$/.test(name)) {
        nextErrors.name = 'Only letters, numbers, spaces, hyphens, and underscores allowed.';
        hasError = true;
      } else if (name.length < 2 || name.length > 32) {
        nextErrors.name = 'Must be 2-32 characters long.';
        hasError = true;
      } else if (policies.some((policy) => policy.name?.toLowerCase() === name.toLowerCase())) {
        nextErrors.name = 'Please choose a unique name.';
        hasError = true;
      }
    }

    setErrors(nextErrors);
    if (hasError) return;

    let nextPolicy;

    if (selectedTemplate && !isEvaluation) {
      const templatePolicy = safeClone(resolveTemplatePolicy(selectedTemplate), {});
      const templateSettings = safeClone(resolveTemplateSettings(selectedTemplate), {});
      const templateFlow = safeClone(resolveTemplateFlow(selectedTemplate), undefined);

      nextPolicy = {
        name,
        policy: templatePolicy,
        settings: {
          ...templateSettings,
          type: policyType,
        },
      };

      if (templateFlow !== undefined) {
        nextPolicy.flow = templateFlow;
        if (!nextPolicy.policy?.flow) {
          nextPolicy.policy = {
            ...nextPolicy.policy,
            flow: safeClone(templateFlow, {}),
          };
        }
      }
    } else {
      nextPolicy = {
        name,
        policy: {},
        settings: {
          type: policyType,
          ...(isEvaluation && selectedLinkedPolicyId
            ? { linked_policy_id: selectedLinkedPolicyId }
            : {}),
        },
      };
    }

    const isTemplatePrefill = Boolean(selectedTemplate && !isEvaluation);
    onSubmitPolicy?.(nextPolicy, { isTemplatePrefill });
    handleCloseDrawer();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleCloseDrawer}>
      <Box sx={{ width: 400, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography level="h4" sx={{ mb: 1 }}>
          Create New Document Flow
        </Typography>
        <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
          Fill in the information of the document flow.
        </Typography>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <FormControl required error={!!errors.type} sx={{ marginBottom: 2 }}>
              <FormLabel>Type</FormLabel>
              <Select
                name="type"
                value={selectedType}
                onChange={handleTypeChange}
                placeholder="Choose the type of the document flow"
              >
                {taskTypesLoading ? (
                  <Option disabled>Loading...</Option>
                ) : (
                  taskTypes.map((taskType) => (
                    <Option key={taskType.type} value={taskType.type}>
                      {taskType.type}
                    </Option>
                  ))
                )}
              </Select>
              {errors.type && (
                <Typography color="danger" level="body-xs" sx={{ mt: 0.5 }}>
                  {errors.type}
                </Typography>
              )}
            </FormControl>

            <FormControl sx={{ marginBottom: 2 }} required error={!!errors.name}>
              <FormLabel>Name</FormLabel>
              {toNormalizedType(selectedType) === 'evaluation' ? (
                <Select
                  name="name"
                  value={selectedPolicyName}
                  onChange={(_, newValue) => {
                    setSelectedPolicyName(newValue || '');
                    const policy = compliancePolicies.find((item) => item.name === newValue);
                    setSelectedLinkedPolicyId(policy?.id || null);
                    clearErrors();
                    setSelectedTemplate(null);
                  }}
                  placeholder="Select a compliance policy"
                >
                  {compliancePolicies.length === 0 ? (
                    <Option disabled>No compliance policies available</Option>
                  ) : (
                    compliancePolicies.map((policy) => (
                      <Option key={policy.id} value={policy.name}>
                        {policy.name}
                      </Option>
                    ))
                  )}
                </Select>
              ) : (
                <Input
                  placeholder="Enter the name of the document flow"
                  name="name"
                  value={policyName}
                  onChange={(event) => {
                    setPolicyName(event.target.value);
                    clearErrors();
                  }}
                  sx={{ marginBottom: 0.5 }}
                  error={!!errors.name}
                />
              )}

              {errors.name && (
                <Typography color="danger" level="body-xs" sx={{ mt: 0.5 }}>
                  {errors.name}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Box
            sx={{
              pt: 1.5,
              mt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography level="title-sm">Templates</Typography>
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.25 }}>
              Choose a template to prefill the flow details.
            </Typography>

            {templatesLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <CircularProgress size="sm" />
                <Typography level="body-sm" color="neutral">
                  Loading templates...
                </Typography>
              </Stack>
            ) : templatesError ? (
              <Typography color="danger" level="body-sm" sx={{ mt: 1 }}>
                {templatesError}
              </Typography>
            ) : filteredTemplates.length === 0 ? (
              <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                {selectedType
                  ? `No templates available for ${selectedType}.`
                  : 'No templates available.'}
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 1.5, maxHeight: 180, overflowY: 'auto', pr: 0.5 }}>
                {filteredTemplates.map((template, index) => {
                  const templateType = resolveTemplateType(template, selectedType || 'Compliance');
                  const isTemplateEvaluation = toNormalizedType(templateType) === 'evaluation';
                  const isSelected =
                    selectedTemplate?.name === template.name &&
                    toNormalizedType(selectedTemplate?.settings?.type) ===
                      toNormalizedType(template.settings?.type);

                  return (
                    <Button
                      key={`${template.name}-${templateType}-${index}`}
                      type="button"
                      variant={isSelected ? 'solid' : 'soft'}
                      color={isSelected ? 'primary' : 'neutral'}
                      onClick={() => handleApplyTemplate(template)}
                      disabled={isTemplateEvaluation}
                      aria-label={`Apply template ${template.name}`}
                      sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 1.25 }}
                    >
                      <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
                        <Typography level="title-sm">{template.name}</Typography>
                        <Typography level="body-xs" sx={{ opacity: 0.8 }}>
                          {templateType}
                        </Typography>
                      </Stack>
                    </Button>
                  );
                })}
              </Stack>
            )}

            {toNormalizedType(selectedType) === 'evaluation' && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                Templates are currently available for non-evaluation policy types.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end',
              pt: 2,
              mt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button variant="plain" color="neutral" onClick={handleCloseDrawer}>
              Cancel
            </Button>
            <Button variant="solid" type="submit" color="primary">
              Next
            </Button>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
};

export default CreatePolicyDrawer;
